import { observer } from "mobx-react-lite";
import { useTranslations } from "next-intl";

import { NodeFieldLabel } from "@/components/doc/right-sidebar/active-node-style/fields/NodeTextField";
import { insertLabel } from "@/input/utils/labels";
import { twMerge } from "@/utils/twMerge";

import type { SymbolNodeModel } from "@/models/nodes/SymbolNodeModel";
import type { LabelNodePosition } from "@corca/data";

type Props = {
  node: SymbolNodeModel;
  contextId: string;
  position?: LabelNodePosition;
  action?: React.ReactNode;
};

export const SymbolPlaceholderIndexField = observer(function SymbolPlaceholderIndexField({
  node,
  contextId,
  position = "right-bottom",
  action,
}: Props) {
  const { doc } = node;
  const t = useTranslations();

  const handleOnClick = (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
    e.preventDefault();

    const { cursor, selection } = doc.cursorContext();

    if (node.parent && selection.isSelected(node.parent)) {
      selection.resetSelection();
      cursor.moveAfterNode(node.parent);
    }

    doc.transact(() => {
      insertLabel(node, position, false);
    }, "SymbolPlaceholderIndexField:handleOnClick");

    const labelNode = node.labelNodes.find((n) => n.position === position);

    if (labelNode) {
      queueMicrotask(() => {
        doc.transact(() => {
          doc.cursorContextId = `${contextId}-${labelNode.id}`;
        }, "SymbolPlaceholderIndexField:changeCursorContextId");
        doc.cursor.moveTo(labelNode.inlineNode, labelNode.inlineNode?.nodes.length ?? 0);
      });
    }
  };

  return (
    <NodeFieldLabel
      as="div"
      label={t(`sidebar.activeNode.fields.labels.${position}`)}
      className={twMerge("py-0.5 pr-0.5 min-h-[28px]", action && "pr-[42px]")}
      onClick={handleOnClick}
    >
      {action && (
        <div
          role="presentation"
          className="absolute right-1 top-0 bottom-0 p-0.5 pl-0 flex items-center"
          onClick={(e) => e.stopPropagation()}
          onMouseDown={(e) => e.stopPropagation()}
        >
          {action}
        </div>
      )}
    </NodeFieldLabel>
  );
});
